/**
 * Shared site-nav enhancement (standalone-pages-shared-nav design § Icons): mounts the real
 * icon-family factories into the server-rendered `data-nav-icon` placeholder spans
 * `templates/site-nav.html` declares. The token → factory map below is the fragment's whole
 * wiring; the walk that mounts it, and the contract that walk holds, are `icon-slots.js`.
 */
import {
  createFingerpost,
  createQuarteredShield,
  createCompass,
  createTabbedTome,
  createLantern,
  createVictorDais,
  createThreeLeggedStool,
  createKeep,
} from "./icons/nav-icons.js";
import { createSpectacles } from "./icons/prompt-icons.js";
import { mountIconSlots } from "./icon-slots.js";

const ICON_SIZE = 20; // Icons draw at 20px — the bar's compact per-entry scale (C2 mock).

// Token → factory map (design § The bar's table). One entry per `data-nav-icon` token.
//
// "keep" (Features, features-family-slice1 design § Inbound links) is `createKeep` — Andre's live
// pick, 2026-08-15, from a five-candidate round: a castle silhouette (three towers over a filled
// gate). The other four candidates from that round have since shipped too (nav-icon-candidate-
// placements task): "fingerpost" (Rules, was "rulebook"), "quartered-shield" (Help, was "scroll"),
// and "lantern" (Your games, was "skat-cards") below — each token renamed for its new mark, since
// the token names the icon shape, not the destination. The retired rulebook/scroll factories stay
// defined in icons/nav-icons.js (the same module their replacements live in), parked on the
// icon-lab Prompt & Feat tab; createSkatCards stays in icons/prompt-icons.js, back to meaning only
// "Pick up Skat" now that this entry wears the lantern. This map is still the single wiring point
// if a future swap is ever needed — nothing else in the wiring depends on the token's name.
export const ICON_BUILDERS = {
  fingerpost: () => createFingerpost(ICON_SIZE),
  "quartered-shield": () => createQuarteredShield(ICON_SIZE),
  compass: () => createCompass(ICON_SIZE),
  "tabbed-tome": () => createTabbedTome(ICON_SIZE),
  lantern: () => createLantern(ICON_SIZE),
  "victor-dais": () => createVictorDais(ICON_SIZE),
  "three-legged-stool": () => createThreeLeggedStool(ICON_SIZE),
  spectacles: () => createSpectacles(ICON_SIZE),
  keep: () => createKeep(ICON_SIZE),
};

export function enhanceSiteNav(root) {
  mountIconSlots(root, "data-nav-icon", ICON_BUILDERS);
}

if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => enhanceSiteNav(document));
}
